import type { IEnvironment } from 'src/services/environment/entities/types';

import { getEarlyErrorBuffer, takeAndDetachEarlyErrorBuffer } from './early-error-buffer';
import type { TEarlyBufferEntry } from './early-error-buffer';
import type { ISentryApi } from './sentry-lite';
import { setSentryApi, setSentryLoadTrigger } from './sentry-lite';

const IDLE_TIMEOUT_MS = 2000;

let loadPromise: Promise<void> | null = null;

/**
 * Loads and initializes the Sentry SDK in a separate chunk, then replays everything the early
 * buffer and the `sentry-lite` queue collected while it was missing. Idempotent; a failed load
 * clears the cached promise so a later trigger (e.g. the next error) can retry.
 */
export function loadSentry(environment: IEnvironment, platformId: string, appVersion: string): Promise<void> {
    loadPromise ??= (async () => {
        // Must detach before SentryService.init runs: breadcrumbsIntegration wraps console.error,
        // and detaching afterwards would wrap rather than remove the early patch.
        const earlyEntries = takeAndDetachEarlyErrorBuffer();
        const { SentryService, getSentryApi } = await import('./sentry.service');

        SentryService.init(environment, platformId, appVersion);

        const api = getSentryApi();

        replayEarlyBuffer(api, earlyEntries);
        setSentryApi(api);
    })().catch((error: unknown) => {
        loadPromise = null;
        throw error;
    });

    return loadPromise;
}

/**
 * Browser-only trigger: whichever comes first — idle time after `load`, or the first error.
 */
export function scheduleSentryLoad(environment: IEnvironment, platformId: string, appVersion: string): void {
    const start = (): void => {
        void loadSentry(environment, platformId, appVersion).catch(() => undefined);
    };

    const buffer = getEarlyErrorBuffer();

    if (buffer) {
        buffer.onError = start;

        if (buffer.entries.length > 0) {
            start();

            return;
        }
    }

    setSentryLoadTrigger(start);

    const scheduleOnIdle = (): void => {
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(start, { timeout: IDLE_TIMEOUT_MS });
        } else {
            setTimeout(start, IDLE_TIMEOUT_MS);
        }
    };

    if (document.readyState === 'complete') {
        scheduleOnIdle();
    } else {
        window.addEventListener('load', scheduleOnIdle, { once: true });
    }
}

/**
 * Uncaught errors and rejections become events. `console.error` becomes an error-level breadcrumb —
 * that is what `breadcrumbsIntegration` does for console output once the SDK is live, so early
 * console output must not turn into extra events.
 */
function replayEarlyBuffer(api: ISentryApi, entries: TEarlyBufferEntry[]): void {
    entries.forEach((entry) => {
        if (entry.kind === 'console') {
            api.addBreadcrumb({
                category: 'console',
                level: 'error',
                message: entry.args.map(stringifyConsoleArg).join(' '),
                timestamp: entry.ts / 1000,
            });

            return;
        }

        api.captureException(entry.value, {
            mechanism: {
                type: entry.kind === 'error' ? 'onerror' : 'onunhandledrejection',
                handled: false,
            },
        });
    });
}

function stringifyConsoleArg(arg: unknown): string {
    if (typeof arg === 'string') {
        return arg;
    }

    if (arg instanceof Error) {
        return `${arg.name}: ${arg.message}`;
    }

    try {
        return JSON.stringify(arg) ?? String(arg);
    } catch {
        return String(arg);
    }
}
